import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader } from "react-bootstrap";
import { Form, Row, Col, Label } from "reactstrap";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import BreadCrumbs from "../../../components/BreadCrumbs";
import ControlledRowTextField from "../../../components/ControlledRowTextField";
import ButtonComponent from "../../../components/ButtonComponent";
import ControlledSelect from "../../../components/ControlledSelect";
import { useCreateAdminMutation, CreateAdminRequest, Role } from "../../../redux/services/adminService";
import { useLazyGetAllRolesQuery } from "../../../redux/services/rolesPermissionService";

interface AdminFormData {
    displayName: string;
    email: string;
    phoneNumber: string;
    roles: { value: string; label: string }[];
}

interface RoleOption {
    value: string;
    label: string;
}

const CreateAdmin = () => {
    const navigate = useNavigate();
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);

    // API hooks
    const [createAdmin] = useCreateAdminMutation();
    const [getAllRoles, { isLoading: isLoadingRoles }] = useLazyGetAllRolesQuery();

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<AdminFormData>({
        defaultValues: {
            displayName: "",
            email: "",
            phoneNumber: "",
            roles: [],
        },
    });

    // Load roles on component mount
    useEffect(() => {
        const loadRoles = async () => {
            try {
                const rolesData = await getAllRoles().unwrap();
                const transformedRoles: RoleOption[] = rolesData.map((role: any) => ({
                    value: role.id,
                    label: role.displayName,
                }));
                setRoleOptions(transformedRoles);
            } catch (error) {
                console.error("Error loading roles:", error);
                toast.error("Failed to load roles");
            }
        };

        loadRoles();
    }, [getAllRoles]);

    const onSubmit = async (data: AdminFormData) => {
        setIsSubmitLoading(true);

        try {
            // Validate required fields
            if (!data.displayName || !data.email || !data.phoneNumber) {
                toast.error("Please fill in all required fields");
                setIsSubmitLoading(false);
                return;
            }

            if (!data.roles || data.roles.length === 0) {
                toast.error("Please select at least one role");
                setIsSubmitLoading(false);
                return;
            }

            // Transform roles data for API
            const transformedRoles: Role[] = data.roles.map((role) => ({
                id: role.value,
                roleName: role.label.toLowerCase().replace(/\s+/g, '_'), 
                displayName: role.label,
            }));

            const adminData: CreateAdminRequest = {
                displayName: data.displayName,
                email: data.email,
                phoneNumber: data.phoneNumber,
                roles: transformedRoles,
            };
            await createAdmin(adminData).unwrap();

            setIsSubmitLoading(false);
            toast.success("Admin user created successfully!");
            navigate("/admins/admins-list");

        } catch (error: any) {
            console.error("Error creating admin:", error);
            setIsSubmitLoading(false);

            if (error?.originalStatus === 409) {
                toast.error("This email is already in use");
            } else if (error?.data?.message) {
                toast.error(error.data.message);
            } else {
                toast.error("Failed to create admin user. Please try again.");
            }
        }
    };

    const handleCancel = () => {
        reset();
        navigate("/admins/admins-list");
    };

    return (
        <>
            <BreadCrumbs hasSubMenus={false} />
            <Card className="mt-3">
                <CardHeader className="bg-white">
                    <h5>Create Admin User</h5>
                </CardHeader>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <CardBody>
                        <ControlledRowTextField
                            inputName="displayName"
                            inputType="text"
                            autoComplete="off"
                            placeholder="Enter full name"
                            label="Full Name"
                            control={control}
                            rules={{
                                required: "Full name is required",
                                minLength: {
                                    value: 2,
                                    message: "Full name must be at least 2 characters"
                                }
                            }}
                            errors={errors}
                        />
                        <Row>
                            <Col lg="4">{""}</Col>
                            <Col lg="8">
                                {errors.displayName && (
                                    <span className="validation-message text-danger">
                                        {String(errors.displayName.message)}
                                    </span>
                                )}
                            </Col>
                        </Row>

                        <hr />

                        <ControlledRowTextField
                            inputName="email"
                            inputType="email"
                            autoComplete="off"
                            placeholder="Enter email address"
                            label="Email Address"
                            control={control}
                            rules={{
                                required: "Email is required",
                                pattern: {
                                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                                    message: "Invalid email format",
                                },
                            }}
                            errors={errors}
                        />
                        <Row>
                            <Col lg="4">{""}</Col>
                            <Col lg="8">
                                {errors.email && (
                                    <span className="validation-message text-danger">
                                        {String(errors.email.message)}
                                    </span>
                                )}
                            </Col>
                        </Row>

                        <hr />

                        <ControlledRowTextField
                            inputName="phoneNumber"
                            inputType="tel"
                            autoComplete="off"
                            placeholder="Enter phone number"
                            label="Phone Number"
                            control={control}
                            rules={{
                                required: "Phone number is required",
                                pattern: {
                                    value: /^[+]?[\d\s\-()]{10,}$/,
                                    message: "Invalid phone number format",
                                },
                            }}
                            errors={errors}
                        />
                        <Row>
                            <Col lg="4">{""}</Col>
                            <Col lg="8">
                                {errors.phoneNumber && (
                                    <span className="validation-message text-danger">
                                        {String(errors.phoneNumber.message)}
                                    </span>
                                )}
                            </Col>
                        </Row>

                        <hr />

                        <Row className="mb-3">
                            <Col lg="4" className="cart-search-label-col">
                                <Label className="form-label">
                                    Roles <span className="text-danger">*</span>
                                </Label>
                            </Col>
                            <Col lg="8">
                                <ControlledSelect
                                    inputName="roles"
                                    control={control}
                                    optionsList={roleOptions}
                                    placeholder={isLoadingRoles ? "Loading roles..." : "Select roles"}
                                    isMulti={true}
                                    isClear={true}
                                    rules={{
                                        required: "Please select at least one role",
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col lg="4">{""}</Col>
                            <Col lg="8">
                                {errors.roles && (
                                    <span className="validation-message text-danger">
                                        {String(errors.roles.message)}
                                    </span>
                                )}
                            </Col>
                        </Row>

                        <hr />

                        <div className="d-flex justify-content-end gap-2 mx-2 my-3">
                            <ButtonComponent
                                titel="Cancel"
                                type="button"
                                onClick={handleCancel}
                            />
                            {isSubmitLoading ? (
                                <ButtonComponent
                                    titel="Creating..."
                                    icon={<FontAwesomeIcon icon={faSpinner} spin />}
                                    isDisabled={true}
                                />
                            ) : (
                                <ButtonComponent
                                    titel="Create Admin"
                                    type="submit"
                                />
                            )}
                        </div>
                    </CardBody>
                </Form>
            </Card>
        </>
    );
};

export default CreateAdmin;